import { PureComponent } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { times } from 'lodash';
import moment from 'moment';
import { WithStyles, withStyles } from '@mui/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import RemoveIcon from '@mui/icons-material/Remove';
import { Skeleton, RadioGroup, FormControlLabel, CircularProgress } from '@mui/material';

// Contexts
import { withTranslate } from '../../../core/TenantProvider';
// Components - Atoms, Organisms,Containers
import APStatistic from '../../../components/atoms/APStatistic';
import APSeriesChart from '../../../components/organisms/APSeriesChart';
import APRadio from '../../../components/atoms/APRadio';
import APDropDownMenu from '../../../components/atoms/APDropDownMenu';
// Files in the same folder
import APCommissionsSectionStyles from './styles/APCommissionsSection.style';
// Utils
import { DecimalFormat } from '../../../core/utils/Formatter';
import { GetCurrencySymbol } from '../../../core/utils/GetCurrenciesData';
import { TriggerGoogleAnalytics4Tag, TriggerGoogleAnalyticsTag } from '../../../core/utils/GoogleAnalyticTag';
import { GetAllAffiliatesIds, GetCurrentAffiliateIds } from '../../../core/utils/Affiliates';
import { GoogleAnalyticTags } from '../../../core/utils/GoogleAnalyticTag/GoogleAnalyticsTags';
import { getReferringCompany } from '../../../core/utils/ReferringCompanyValueGetter/ReferringCompanyValueGetter';
import { ORGANIZATION_CURRENCY_MAPPING, DEFAULT_BASE_CURRENCY } from '../../../core/utils/Constants/Constants';
import { GoogleAnalytics4Tags } from '../../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';
// Redux
import { ReducerState } from '../../../redux/modules';
import { affiliate, post } from '../../../redux/modules/session/actions';
import { actions as AccountActions } from '../../../redux/modules/account';
import { actions as servicesDowntimeAction } from '../../../redux/modules/servicesDowntime';

let renderMiniStat_balance = '',
	renderMiniStat_monthly = '',
	renderMiniStat_quarterly = '',
	renderMiniStat_Paid_Monthly = '',
	renderMiniStat_Paid_Quarterly = '';

let renderMiniStat_balance_ga4 = '',
	renderMiniStat_monthly_ga4 = '',
	renderMiniStat_quarterly_ga4 = '',
	renderMiniStat_Paid_Monthly_ga4 = '',
	renderMiniStat_Paid_Quarterly_ga4 = '',
	renderMiniStat_Module_Name_ga4 = '';

interface APCommissionsSectionProps extends WithStyles<typeof APCommissionsSectionStyles> {
	affiliateDetails: any,
	get: Function,
	postWrapper: Function,
	getAccountCommissions: Function,
	results: any,
	resultsComm: any,
	loadingState: any,
	graphDataLoadingState: any,
	settings: any,
	translate: any,
	baseCurrency: string,
	getServicesErrorObj: Function,
	selectedAffiliate: string,
	accountCommissionsDataReset: Function
}

class APCommissionsSection extends PureComponent<APCommissionsSectionProps, {}> {

	private _isMounted: boolean;

	state = {
		view: 'generated',
		setCurrency: DEFAULT_BASE_CURRENCY.CODE,
		setCurrencySymbol: DEFAULT_BASE_CURRENCY.SYMBOL,
		currencies: [DEFAULT_BASE_CURRENCY.CODE]
	};

	constructor(props: APCommissionsSectionProps) {
		super(props);

		this.onChangeView = this.onChangeView.bind(this);
		this.onCurrChange = this.onCurrChange.bind(this);
		this._isMounted = false;
	}

	updateDimensions = () => {
		this._isMounted && this.getUserCurrencies();
	}

	componentDidMount() {
		var resizeTimer;

		this._isMounted = true;
		this._isMounted && this.getUserCurrencies();
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				this.updateDimensions();
			}, 500);
		});

		const currentURL = window.location.href;
		const currentModule = currentURL.indexOf("/account") >= 0 ? 'accounts' : currentURL.indexOf("/commissions") >= 0 ? 'commissions' : '';

		if (GoogleAnalyticTags[currentModule]) {
			renderMiniStat_balance = GoogleAnalyticTags[currentModule].click.tooltip.balance;
			renderMiniStat_monthly = GoogleAnalyticTags[currentModule].click.tooltip.generatedLastMonth;
			renderMiniStat_quarterly = GoogleAnalyticTags[currentModule].click.tooltip.generatedLastQuarter;
			renderMiniStat_Paid_Monthly = GoogleAnalyticTags[currentModule].click.tooltip.generatedLastMonth;
			renderMiniStat_Paid_Quarterly = GoogleAnalyticTags[currentModule].click.tooltip.paidLastQuarter;
		}
		if (GoogleAnalytics4Tags[currentModule]) {
			const currentModuleTooltipGA4Config = GoogleAnalytics4Tags[currentModule].click.tooltip;
			renderMiniStat_balance_ga4 = currentModuleTooltipGA4Config.balance;
			renderMiniStat_monthly_ga4 = currentModuleTooltipGA4Config.generatedLastMonth;
			renderMiniStat_quarterly_ga4 = currentModuleTooltipGA4Config.generatedLastQuarter;
			renderMiniStat_Paid_Monthly_ga4 = currentModuleTooltipGA4Config.generatedLastMonth;
			renderMiniStat_Paid_Quarterly_ga4 = currentModuleTooltipGA4Config.paidLastQuarter;
			renderMiniStat_Module_Name_ga4 = GoogleAnalytics4Tags[currentModule]?.name ? GoogleAnalytics4Tags[currentModule].name : '';
		}
	}

	componentDidUpdate(prevProps: Readonly<APCommissionsSectionProps>, prevState: Readonly<{}>, snapshot?: any): void {
		if (prevProps.selectedAffiliate !== this.props.selectedAffiliate) {
			this.props.accountCommissionsDataReset();
			this.getUserCurrencies().then(() => {
				console.log('Get User Currencies success');
			}).catch((error) => {
				console.log('Error in Get User Currencies');
			});
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
		window.removeEventListener('resize', this.updateDimensions);
	}

	onChangeView(evt: any) {
		const {
			target: {
				name,
				value
			}
		} = evt;

		const {
			selectedAffiliate,
			affiliateDetails,
			getAccountCommissions,
		} = this.props;

		const currentURL = window.location.href;
		const currentModule = currentURL.indexOf("/account") >= 0 ? 'accounts' : currentURL.indexOf("/commissions") >= 0 ? 'commissions' : '';

		if (GoogleAnalyticTags[currentModule]) {
			TriggerGoogleAnalyticsTag(
				GoogleAnalyticTags.eventAction.click,
				GoogleAnalyticTags[currentModule].click.radioButton[value]
			);
			TriggerGoogleAnalytics4Tag(
				GoogleAnalytics4Tags[currentModule].name,
				GoogleAnalytics4Tags.eventAction.click,
				GoogleAnalytics4Tags[currentModule].click.radioButton[value]
			);
		}

		const currentAffiliateIds = GetCurrentAffiliateIds(GetAllAffiliatesIds(affiliateDetails), selectedAffiliate);

		this.setState({
			[name]: value
		}, () => {
			getAccountCommissions(currentAffiliateIds, this.state.setCurrency, value);
		});
	}

	async getUserCurrencies() {
		const {
			postWrapper,
			getAccountCommissions,
			getServicesErrorObj,
			affiliateDetails,
			selectedAffiliate
		} = this.props;

		const currentAffiliateIds: string[] = GetCurrentAffiliateIds(GetAllAffiliatesIds(affiliateDetails), selectedAffiliate);

		await postWrapper(`/affiliate-portal/AffiliateDetails/getCurrencies`, { affiliate_number: affiliate() })
			.then((resp: any) => {
				if (resp.data && resp.data.Currencies) {
					let currencies: any = [];
					resp.data.Currencies.forEach((val: any) => {
						val !== '000' && currencies.push({ label: val, value: val });
					});

					const orgBaseCurrency = ORGANIZATION_CURRENCY_MAPPING[getReferringCompany()];
					const baseCurrency = resp.data.Currencies.includes(orgBaseCurrency) ? orgBaseCurrency
						: currencies.length > 0 ? currencies[0].value : orgBaseCurrency;
					const firstVal = baseCurrency ? baseCurrency : DEFAULT_BASE_CURRENCY.CODE;
					const setSymbol = GetCurrencySymbol(firstVal);

					this._isMounted && this.setState({
						setCurrency: firstVal,
						setCurrencySymbol: setSymbol,
						currencies
					}, () => {
						getAccountCommissions(currentAffiliateIds, firstVal, this.state.view);
					});

				} else {
					console.error('Error no currencies');
				}
			})
			.catch((error: any) => {
				console.error(error?.message);
				getServicesErrorObj(error);
			})
	}

	onCurrChange(type: string) {
		const {
			selectedAffiliate,
			affiliateDetails,
			getAccountCommissions
		} = this.props;

		const currentAffiliateIds = GetCurrentAffiliateIds(GetAllAffiliatesIds(affiliateDetails), selectedAffiliate);

		this.setState({
			setCurrency: type,
			setCurrencySymbol: GetCurrencySymbol(type)
		}, () => {
			getAccountCommissions(currentAffiliateIds, type, this.state.view);
		});
	}

	renderMiniStat(config: any) {
		const {
			classes,
			translate
		} = this.props;

		const {
			displayIcon = true,
			name,
			gaTagToolTips,
			ga4ToolTipTags,
			ga4ModuleNameTag,
			primary,
			secondary,
			currFigure,
			prevFigure,
			secondaryText
		} = config;

		const summary = (
			<span className={classes.miniStatSummary}>
				<span className={classes.miniStatValue}>{secondary}</span>
				<span className={classes.miniStatLabel}>
					{translate(`account.ministat.${name}.summary`)}
				</span>
			</span>
		);

		const summaryText = (
			<span className={classes.miniStatSummary}>
				<span className={classes.miniStatLabelText}>
					{secondaryText}
				</span>
			</span>
		);

		let icon;
		if (displayIcon) {
			if (currFigure > prevFigure) {
				icon = <ArrowDropUpIcon className={classes.iconUp} />
			} else if (currFigure < prevFigure) {
				icon = <ArrowDropDownIcon className={classes.iconDown} />
			} else {
				icon = <RemoveIcon className={classes.iconNoChange} />
			}
		}

		let label = translate(`commissions.ministat.${name}.name`);
		if (translate.has(`commissions.ministat.${name}.${this.state.view}.name`)) {
			label = translate(`commissions.ministat.${name}.${this.state.view}.name`);
		}

		let toolTipMessage = (name === "commisioncurrentmonth") ?
			false : translate(`account.ministat.${name}.${this.state.view}.tooltip`);

		return (
			<APStatistic
				icon={icon}
				mini
				name={label}
				gaTagToolTips={gaTagToolTips}
				ga4ToolTipTags={ga4ToolTipTags}
				ga4ModuleNameTag={ga4ModuleNameTag}
				summary={(secondary) ? summary : (secondaryText) ? summaryText : ''}
				value={primary}
				toolTipMessage={toolTipMessage}
			/>
		)
	}

	render() {
		const {
			classes,
			resultsComm,
			graphDataLoadingState,
			translate
		} = this.props;

		const { setCurrencySymbol, setCurrency, currencies, view } = this.state;

		let headlineLoaders: any = [];

		const seriesChartLabelHeading: string = translate(`account.ministat.show.${view === 'generated' ? 'generated' : 'paid'}`);

		times(3, (i) => {
			headlineLoaders.push(<div key={`skeleton-${i}`} className={classes.headlinesLoader}>
				<Skeleton width={`100%`} variant="text" height={29} />
				<Skeleton width={`100%`} variant="text" height={28} />
			</div>);
		});

		const commissionDate: moment.Moment = resultsComm?.commissions && moment(resultsComm.commissions.date);
		const commissionDateDD: string = commissionDate?.format('Do');
		const commissionMonthAndYear: string = commissionDate?.format('MMMM YYYY');
		const formattedCommissionDate: string = commissionDate ? commissionDateDD + ' ' + commissionMonthAndYear?.charAt(0)?.toUpperCase() + commissionMonthAndYear?.slice(1) : '';

		return (
			<>
				<div className={classes.payment}>
					<div className={classes.paymentDateInfo}>
						<span className={classes.paymentInfo}>
							{translate('account.section.commission.payment.info')}
						</span>
						<span className={classes.paymentDate}>
							{formattedCommissionDate}
						</span>
					</div>

					<div className={classes.paymentOptions}>
						<div className={classes.paymentOptionsViewSelector}>
							<span>{translate('account.ministat.show.label')}</span>
							<RadioGroup className={classes.viewSelectorRadio} value={this.state.view} name={'view'} onChange={this.onChangeView}>
								<FormControlLabel control={<APRadio />} label={`${translate('account.ministat.show.generated')}`} value="generated" />
								<FormControlLabel control={<APRadio />} label={`${translate('account.ministat.show.paid')}`} value="paid" />
							</RadioGroup>
						</div>
						{currencies &&
							<APDropDownMenu
								className={classes.paymentOptionsDropDownMenu}
								label={setCurrency}
								isFlag={true}
								onChange={this.onCurrChange}
								options={currencies}
							/>
						}
					</div>
				</div>



				{graphDataLoadingState === 'ACCOUNT_COMMISSIONS_LOADED' ? (
					resultsComm && resultsComm.commissions ? (
						<>
							<div className={classes.headlines}>
								{this.renderMiniStat({
									name: 'balance',
									gaTagToolTips: renderMiniStat_balance,
									ga4ToolTipTags: renderMiniStat_balance_ga4,
									ga4ModuleNameTag: renderMiniStat_Module_Name_ga4,
									primary: `${setCurrencySymbol}${DecimalFormat(resultsComm.commissions.balancecarriedforward)}`,
									displayIcon: false,
									secondaryText: `${translate('account.ministat.balancecarryforward.summary')}`
								})}
								{this.renderMiniStat({
									name: 'commisioncurrentmonth',
									gaTagToolTips: this.state.view === 'generated' ? renderMiniStat_monthly : renderMiniStat_Paid_Monthly,
									ga4ToolTipTags: this.state.view === 'generated' ? renderMiniStat_monthly_ga4 : renderMiniStat_Paid_Monthly_ga4,
									ga4ModuleNameTag: renderMiniStat_Module_Name_ga4,
									primary: `${setCurrencySymbol}${DecimalFormat(resultsComm.commissions[`${this.state.view}commisioncurrentmonth`])}`,
									secondaryText: `${translate(`account.ministat.commisioncurrentmonth.${this.state.view}.summary`)}`,
									displayIcon: false
								})}
								{this.renderMiniStat({
									displayIcon: true,
									name: 'quarterly',
									gaTagToolTips: this.state.view === 'generated' ? renderMiniStat_quarterly : renderMiniStat_Paid_Quarterly,
									ga4ToolTipTags: this.state.view === 'generated' ? renderMiniStat_quarterly_ga4 : renderMiniStat_Paid_Quarterly_ga4,
									ga4ModuleNameTag: renderMiniStat_Module_Name_ga4,
									primary: `${setCurrencySymbol}${DecimalFormat(resultsComm.commissions[`${this.state.view}Quarterly`])}`,
									secondary: resultsComm.commissions[`${this.state.view}QuarterlyChange`],
									currFigure: resultsComm.commissions[`${this.state.view}Quarterly`],
									prevFigure: resultsComm.commissions[`${this.state.view}LastQuarterOfPrevYear`],
								})}
							</div>
						</>
					) : (
						<div className={classes.headlines}>
							{this.renderMiniStat({
								name: 'balance',
								primary: `${setCurrencySymbol}${DecimalFormat(0)}`
							})}
							{this.renderMiniStat({
								displayIcon: true,
								name: 'commisioncurrentmonth',
								primary: `${setCurrencySymbol}${DecimalFormat(0)}`,
								secondary: 0
							})}
							{this.renderMiniStat({
								displayIcon: true,
								name: 'quarterly',
								primary: `${setCurrencySymbol}${DecimalFormat(0)}`,
								secondary: 0
							})}
						</div>
					)
				) : (<div className={classes.headlines}>{headlineLoaders}</div>)}

				{graphDataLoadingState === 'ACCOUNT_COMMISSIONS_LOADED' ? (resultsComm && resultsComm.series &&
					<div>
						<APSeriesChart labelHeading={seriesChartLabelHeading} series={resultsComm.series} yPrefix={setCurrencySymbol} height={300} width="100%" />
					</div>
				) : (
					<div className={classes.graphLoader}>
						<CircularProgress className={classes.graphLoaderSpinner} />
					</div>
				)}
			</>
		);
	}
}

const mapStateToProps = (state: ReducerState) => ({
	affiliateDetails: state.user.affiliateDetails,
	results: state.account.results,
	resultsComm: state.account.resultsComm,
	loadingState: state.account.loadingState,
	graphDataLoadingState: state.account.graphDataLoadingState
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	postWrapper: post,
	getAccountCommissions: AccountActions.getAccountCommissions,
	getServicesErrorObj: servicesDowntimeAction.getServicesErrorObj,
	accountCommissionsDataReset: AccountActions.accountCommissionsDataReset
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
	withTranslate(
		withStyles(APCommissionsSectionStyles)(APCommissionsSection)
	)
);
